import { Routes, Route, Navigate } from 'react-router-dom';
import { useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useBootstrap } from '../context/BootstrapContext';
import AppLayout from '../components/AppLayout';
import NotFound from '../pages/NotFound';
import HomePage from '../pages/Home/HomePage';
import TowerPage from '../pages/Tower/TowerPage';
import Settings from '../pages/Settings/Settings';
import Suporte from '../pages/Support/Suporte';
import Protocolos from '../pages/Protocols/Protocolos';
import LicenciamentoPage from '../pages/Licenciamento/LicenciamentoPage';
import ExperimentsPage from '../pages/Historico/ExperimentsPage';
import GraficoTabsPage from '../pages/Historico/GraficoTabsPage';
import TemplatePages from '../pages/Template/TemplatePages';
import TemplateEditorPage from '../pages/Template/TemplateEditorPage';
import AnalisePage from '../pages/Analise/AnalisePage';
import ParametrosPage from '../pages/Parametros/ParametrosPage';
import DeviceDashboardPage from '../pages/Tower/DeviceDashboardPage';
import PropriedadePage from '../pages/Leite/PropriedadePage';
import ColetaPage from '../pages/Coleta/ColetaPage';
import ColetaDetalhePage from '../pages/Coleta/ColetaDetalhePage';
import PontosColetaPage from '../pages/PontosColeta/PontosColetaPage';
import AcoesPage from '../pages/Acoes/AcoesPage';
import ColostroPage from '../pages/Colostro/ColostroPage';
import { HIDDEN_MENU_ROUTES } from '../components/Menu/Menu';
import RequireTenantAuth from './RequireTenantAuth';
import ClientePendenteGuard from '../components/ClientePendenteGuard';

const allAppRoutes = [
    { path: 'home', element: <HomePage />, bootstrapRoute: '/home' },
    { path: 'amostras', element: <ExperimentsPage />, bootstrapRoute: '/amostras' },
    // Os gráficos abrem a partir de Amostras e do detalhe das páginas de coleta: a conta
    // LEITE pode não ter Amostras no menu.
    { path: 'graficos/:experimentId', element: <GraficoTabsPage />, bootstrapRoute: ['/amostras', '/leite', '/colostro', '/agua'] },
    { path: 'graficos', element: <Navigate to="../amostras" replace />, bootstrapRoute: '/amostras' },
    { path: 'em-andamento', element: <Navigate to="../amostras" replace />, bootstrapRoute: '/amostras' },
    { path: 'finalizadas', element: <Navigate to="../amostras" replace />, bootstrapRoute: '/amostras' },
    { path: 'bioailabs', element: <TowerPage />, bootstrapRoute: '/bioailabs' },
    { path: 'bioailabs/:towerId', element: <TowerPage />, bootstrapRoute: '/bioailabs' },
    { path: 'bioailab/:numeroSerie', element: <TowerPage />, bootstrapRoute: '/bioailabs' },
    { path: 'bioailabs/device/:serialNumber/dashboard', element: <DeviceDashboardPage />, bootstrapRoute: '/bioailabs' },
    { path: 'protocolos', element: <Protocolos />, bootstrapRoute: '/protocolos' },
    { path: 'licenciamento', element: <LicenciamentoPage />, bootstrapRoute: '/licenciamento' },
    { path: 'templates', element: <TemplatePages />, bootstrapRoute: '/templates' },
    { path: 'templates/:id/editor', element: <TemplateEditorPage />, bootstrapRoute: '/templates' },
    { path: 'configuracoes', element: <Settings />, bootstrapRoute: '/configuracoes' },
    { path: 'analises', element: <AnalisePage />, bootstrapRoute: '/analises' },
    { path: 'parametros', element: <ParametrosPage />, bootstrapRoute: '/parametros' },
    { path: 'suporte', element: <Suporte />, bootstrapRoute: '/suporte' },

    // Plano LEITE. Como todas as outras, só existem para a conta cujo menu (configurado
    // no IOT) tiver um item com esta rota — não precisam de trava por plano. `key`
    // separa as três páginas de coleta: sem ela, navegar de uma para outra reaproveitaria
    // o estado (filtros, página) da anterior.
    { path: 'propriedade', element: <PropriedadePage />, bootstrapRoute: '/propriedade' },
    { path: 'leite', element: <ColetaPage key="LEITE" pagina="LEITE" />, bootstrapRoute: '/leite' },
    { path: 'leite/:amostraId', element: <ColetaDetalhePage pagina="LEITE" />, bootstrapRoute: '/leite' },
    // Demonstracao (sem analise de colostro nos dispositivos ainda): ver pages/Colostro/colostro.js.
    { path: 'colostro', element: <ColostroPage />, bootstrapRoute: '/colostro' },
    { path: 'colostro/:amostraId', element: <ColetaDetalhePage pagina="COLOSTRO" />, bootstrapRoute: '/colostro' },
    { path: 'agua', element: <ColetaPage key="AGUA" pagina="AGUA" />, bootstrapRoute: '/agua' },
    { path: 'agua/:amostraId', element: <ColetaDetalhePage pagina="AGUA" />, bootstrapRoute: '/agua' },
    { path: 'pontos-coleta', element: <PontosColetaPage />, bootstrapRoute: '/pontos-coleta' },
    { path: 'acoes', element: <AcoesPage />, bootstrapRoute: '/acoes' },
];

function GuardedAppLayout() {
    return (
        <ClientePendenteGuard>
            <AppLayout />
        </ClientePendenteGuard>
    );
}

export default function InnerRoutes() {
    const { bootstrap, loading } = useBootstrap();
    const allowedRoutes = useMemo(() => {
        if (!bootstrap?.menu?.itensMenus) {
            return [];
        }
        const allowedBootstrapRoutes = new Set(
            bootstrap.menu.itensMenus.map(item => item.rotas)
        );

        return allAppRoutes.filter(route => {
            const requiredRoutes = Array.isArray(route.bootstrapRoute)
                ? route.bootstrapRoute
                : [route.bootstrapRoute];
            return requiredRoutes.some(r => allowedBootstrapRoutes.has(r));
        });
    }, [bootstrap]);

    // Para onde vai quem entra em `/<tenant>`: `home` quando o menu da conta tem Home;
    // senão, o PRIMEIRO item do menu, na mesma ordem da barra lateral. O redirect era
    // fixo em `home`, e uma conta sem Home (o plano LEITE usa `/propriedade` como página
    // inicial) caía num 404 logo depois do login.
    const rotaInicial = useMemo(() => {
        const liberadas = new Set(allowedRoutes.map((r) => r.path));
        if (liberadas.has('home')) return 'home';
        const primeiro = (bootstrap?.menu?.itensMenus || [])
            .filter((i) => !HIDDEN_MENU_ROUTES.includes(i.rotas))
            .map((i) => String(i.rotas || '').replace(/^\//, ''))
            .find((rota) => liberadas.has(rota));
        return primeiro || 'home';
    }, [allowedRoutes, bootstrap]);

    if (loading && allowedRoutes.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 'calc(100vh - 64px)' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!bootstrap) {
        return null;
    }

    return (
        <Routes>
            <Route element={<RequireTenantAuth />}>
                <Route element={<GuardedAppLayout />}>
                    <Route path="" element={<Navigate to={rotaInicial} replace />} />
                    {/* Conta sem Home no menu (plano LEITE abre em /propriedade): um
                        /home antigo — favorito, histórico do navegador, o retorno do
                        login para a "página exata" — vai para a página inicial da
                        conta em vez de cair no 404. */}
                    {rotaInicial !== 'home' && (
                        <Route path="home" element={<Navigate to={`../${rotaInicial}`} replace />} />
                    )}

                    {allowedRoutes.map(({ path, element }) => (
                        <Route key={path} path={path} element={element} />
                    ))}

                    <Route path="*" element={<NotFound />} />
                </Route>
                <Route path="*" element={<NotFound />} />
            </Route>
        </Routes>
    );
}
